export type Locale = 'en' | 'ar' | 'ur';

export const locales: Locale[] = ['en', 'ar', 'ur'];
export const defaultLocale: Locale = 'en';

type TranslationMap = Record<string, string>;

export const translations: Record<Locale, TranslationMap> = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.booking': 'Location Visits',
    'nav.about': 'About Us',
    'nav.contact': 'Contact',
    'auth.signIn': 'Sign in',
    'auth.createAccount': 'Create account',
    'auth.account': 'Account',
    'auth.signOut': 'Sign out',
    'auth.dashboard': 'Dashboard',

    // Location Selection Dialog
    'locationDialog.tag': 'Location setup',
    'locationDialog.title': 'Select your location',
    'locationDialog.description':
      'Choose a city and preferred plant to personalize your catalog.',
    'locationDialog.step.city': 'City',
    'locationDialog.step.factory': 'Plant',
    'locationDialog.step.cityTitle': 'Choose your city',
    'locationDialog.step.citySubtitle':
      'Pick the city to see available plants.',
    'locationDialog.step.factoryTitle': 'Choose a plant',
    'locationDialog.step.factorySubtitle':
      'Select the plant you prefer to order from.',
    'locationDialog.empty.cities': 'No cities available.',
    'locationDialog.empty.factories': 'No plants available for this city.',
    'locationDialog.error.cities': 'Failed to load cities.',
    'locationDialog.error.factories': 'Failed to load plants.',
    'locationDialog.selectedCity': 'Selected city',
    'locationDialog.actions.changeCity': 'Change city',
    'locationDialog.actions.back': 'Back',
    'locationDialog.actions.close': 'Close',
    'locationDialog.helper': 'Selection updates the products you see.',

    // Header
    'header.selectFactory': 'Select Plant',
    'header.searchFactories': 'Search plants...',
    'header.allFactories': 'All Plants',

    // Hero Section
    'hero.title': 'United Feed Company',
    'hero.subtitle':
      'Reliable livestock and poultry feed production with factory-to-customer logistics across Saudi Arabia',
    'hero.cta.primary': 'Explore Products',
    'hero.cta.secondary': 'Book Plant Visit',
    'hero.stat1.value': '39',
    'hero.stat1.label': 'Tons Daily Capacity',
    'hero.stat2.value': '3',
    'hero.stat2.label': 'Manufacturing Plants',
    'hero.stat3.value': '50+',
    'hero.stat3.label': 'Years of Excellence',
    'hero.stat4.value': '0',
    'hero.stat4.label': 'Accidents',

    // About Page
    'about.hero.eyebrow': 'About United Feed Company',
    'about.hero.title':
      'Supporting food security through reliable feed and logistics',
    'about.hero.subtitle':
      'United Feed Company is a Saudi feed manufacturer serving livestock and poultry customers with reliable products and logistics.',
    'about.hero.since': 'Established 1398 AH',
    'about.certs.title': 'Our Certifications',
    'about.certs.subtitle':
      'Recognized globally for quality, safety, and compliance.',
    'about.certs.downloadPdf': 'Download PDF',
    'about.certs.viewCertificate': 'View Certificate',
    'about.certs.empty': 'No certifications available at this time.',
    'about.certs.loadError': 'Could not load certifications.',
    'about.stats.title': 'Key Facts',
    'about.stats.subtitle':
      'A snapshot of our scale and production capability.',
    'about.story.title': 'Our Story',
    'about.story.subtitle':
      'A legacy of reliability, built on Saudi quality standards.',
    'about.story.paragraph1':
      'United Feed Company operates modern feed production and factory-to-customer logistics serving customers across Saudi Arabia.',
    'about.story.paragraph2':
      'We supply livestock and poultry feed with consistent quality and dependable factory-to-customer logistics.',
    'about.story.paragraph3':
      'Our people, processes, and plants are aligned around safety, operational excellence, and customer success.',
    'about.vision.title': 'Our Vision',
    'about.vision.description':
      'To be the Kingdom’s trusted feed partner through quality, reliability, and sustainability.',
    'about.mission.title': 'Our Mission',
    'about.mission.description':
      'Deliver reliable products and exceptional service while empowering our teams and partners.',
    'about.values.title': 'Our Values',
    'about.values.subtitle': 'How we show up for customers and communities.',
    'about.values.customer.title': 'Customer Centric',
    'about.values.customer.description':
      'We design service and delivery around customer outcomes.',
    'about.values.integrity.title': 'Integrity',
    'about.values.integrity.description':
      'We do the right thing with transparency and accountability.',
    'about.values.excellence.title': 'Excellence',
    'about.values.excellence.description':
      'We raise the bar on quality, safety, and performance.',
    'about.values.sustainability.title': 'Sustainability',
    'about.values.sustainability.description':
      'We reduce impact through efficient, responsible operations.',
    'about.values.teamwork.title': 'Teamwork',
    'about.values.teamwork.description':
      'We collaborate across plants and teams to deliver reliably.',
    'about.values.innovation.title': 'Innovation',
    'about.values.innovation.description':
      'We invest in modern processes and continuous improvement.',
    'about.operations.title': 'Manufacturing Footprint',
    'about.operations.subtitle':
      'Integrated production and logistics at the Yanbu feed mill.',
    'about.operations.plantJazan.name': 'Yanbu Feed Production',
    'about.operations.plantJazan.description':
      'Yanbu feed production and logistics serving customers across Saudi Arabia.',
    'about.operations.plantYanbu.name': 'Yanbu Bulk Loading',
    'about.operations.plantYanbu.description':
      'Bulk feed silos and loading operations for the rented bulker fleet.',
    'about.operations.plantTihama.name': 'Yanbu Bagged Feed',
    'about.operations.plantTihama.description':
      'Modern production lines designed for reliability and sustainable output.',
    'about.commitments.title': 'Quality, Safety, and Sustainability',
    'about.commitments.subtitle': 'Standards that guide every ton we produce.',
    'about.commitments.quality.title': 'Quality Assurance',
    'about.commitments.quality.description':
      'Rigorous testing and process control across every batch.',
    'about.commitments.safety.title': 'Safety First',
    'about.commitments.safety.description':
      'Protecting our teams and partners with disciplined safety systems.',
    'about.commitments.sustainability.title': 'Sustainable Operations',
    'about.commitments.sustainability.description':
      'Efficiency upgrades and responsible resource use across plants.',

    // Contact Page
    'contact.hero.eyebrow': 'Contact',
    'contact.hero.title': 'Talk to the team that keeps your projects moving',
    'contact.hero.subtitle':
      'Share your requirements and our team will help with the right product, schedule, and support.',
    'contact.hero.ctaPrimary': 'Call Sales',
    'contact.hero.ctaSecondary': 'Send a Message',
    'contact.hero.highlight.sales': 'Sales inquiries',
    'contact.hero.highlight.support': 'Technical support',
    'contact.hero.highlight.partnerships': 'Partnerships',
    'contact.hero.panelTitle': 'Contact Hub',
    'contact.hero.panelBadge': "We're here",

    'contact.cards.phone.title': 'Phone',
    'contact.cards.phone.value': '+966-XX-XXX-XXXX',
    'contact.cards.phone.description': 'Customer care and sales',
    'contact.cards.email.title': 'Email',
    'contact.cards.email.value': 'info@ufc.sa',
    'contact.cards.email.description': 'General inquiries',
    'contact.cards.location.title': 'Coverage',
    'contact.cards.location.value': 'Yanbu · Saudi Arabia',
    'contact.cards.location.description':
      'Serving the southern and western regions',

    // Contact Form
    'contact.form.title': 'Send us a message',
    'contact.form.subtitle':
      'Fill in the form below and our team will get back to you within 24 hours.',
    'contact.form.name': 'Full Name',
    'contact.form.namePlaceholder': 'Your full name',
    'contact.form.email': 'Email Address',
    'contact.form.emailPlaceholder': 'your@email.com',
    'contact.form.phone': 'Phone Number',
    'contact.form.phonePlaceholder': '+966 5X XXX XXXX',
    'contact.form.phoneOptional': 'Optional',
    'contact.form.subject': 'Subject',
    'contact.form.subjectPlaceholder': 'Select a subject',
    'contact.form.subject.generalInquiry': 'General Inquiry',
    'contact.form.subject.productInfo': 'Product Information',
    'contact.form.subject.technicalSupport': 'Technical Support',
    'contact.form.subject.partnership': 'Partnership',
    'contact.form.subject.other': 'Other',
    'contact.form.message': 'Message',
    'contact.form.messagePlaceholder': 'How can we help you?',
    'contact.form.submit': 'Send Message',
    'contact.form.submitting': 'Sending...',
    'contact.form.success.title': 'Message Sent!',
    'contact.form.success.description':
      'Thank you for reaching out. We will get back to you at {{email}} within 24 hours.',
    'contact.form.success.sendAnother': 'Send Another Message',
    'contact.form.error.nameRequired': 'Full name is required',
    'contact.form.error.emailRequired': 'Email address is required',
    'contact.form.error.emailInvalid': 'Please enter a valid email address',
    'contact.form.error.subjectRequired': 'Please select a subject',
    'contact.form.error.messageRequired': 'Message is required',
    'contact.form.error.messageTooShort':
      'Message must be at least 10 characters',
    'contact.form.error.failed':
      'Failed to send your message. Please try again.',

    // Support Tickets (E-commerce)
    'support.successTitle': 'Ticket Submitted!',
    'support.successMessage':
      "We've received your request and will get back to you shortly.",
    'support.submitAnother': 'Submit Another',
    'support.fieldSubject': 'Subject',
    'support.subjectPlaceholder': 'Brief summary of your issue',
    'support.fieldCategory': 'Category',
    'support.selectCategory': 'Select category',
    'support.categoryOrder': 'Order Issue',
    'support.categoryDelivery': 'Delivery Issue',
    'support.categoryPayment': 'Payment Issue',
    'support.categoryAccount': 'Account Issue',
    'support.categoryGeneral': 'General',
    'support.categoryOther': 'Other',
    'support.fieldDescription': 'Description',
    'support.descriptionPlaceholder': 'Describe your issue in detail...',
    'support.fieldEmail': 'Contact Email',
    'support.emailPlaceholder': 'email@example.com',
    'support.fieldPhone': 'Contact Phone',
    'support.phonePlaceholder': '+966 5X XXX XXXX',
    'support.createError': 'Failed to submit. Please try again.',
    'support.submitting': 'Submitting...',
    'support.submit': 'Submit Ticket',

    // Not Found
    'ui.notFound.title': '404 - Page Not Found',
    'ui.notFound.description': 'Sorry, the page you are looking for does not exist.',
    'ui.notFound.goHome': 'Go Home',

    // Products
    'products.title': 'Our Feed Products',
    'products.subtitle':
      'High-quality livestock and poultry feed for farms across Saudi Arabia',
    'products.viewDetails': 'View Details',
    'products.addToCart': 'Add to Cart',
    'products.selectVariation': 'Select Package',
    'products.inStock': 'In Stock',
    'products.category': 'Category',
    'products.certificates.title': 'Product Certificates',
    'products.certificates.downloadPdf': 'Download PDF',
    'products.certificates.empty':
      'No certificates available for this product.',
    'cart.title': 'Shopping Cart',
    'cart.itemsInCart': 'items in your cart',
    'cart.orderSummary': 'Order Summary',
    'cart.total': 'Total',
    'cart.proceedToCheckout': 'Proceed to Checkout',
    'cart.continueShopping': 'Continue Shopping',
    'cart.empty.title': 'Your cart is empty',
    'cart.empty.description': 'Add some products to your cart to get started',
    'cart.empty.browseProducts': 'Browse Products',

    // Plant Visits
    'booking.title': 'Visit Our Facilities',
    'booking.subtitle':
      'Schedule a visit to experience our world-class facilities',
    'booking.book': 'Book Visit',
    'booking.duration': 'Duration',
    'booking.capacity': 'Group Size',
    'booking.price': 'Price',
    'booking.free': 'Free',
    'booking.popular': 'Most Popular',
    'booking.errors.firstNameRequired': 'First name is required',
    'booking.errors.lastNameRequired': 'Last name is required',
    'booking.errors.userIdRequired': 'ID number is required',
    'booking.errors.userIdInvalid': 'Please enter a valid ID number',
    'booking.errors.emailInvalid': 'Please enter a valid email address',
    'booking.errors.phoneRequired': 'Phone number is required',
    'booking.errors.phoneInvalid': 'Please enter a valid phone number',
    'booking.errors.locationRequired': 'Location is required',
    'booking.errors.hostNameRequired': 'Host name is required',
    'booking.errors.hostPhoneRequired': 'Host phone is required',
    'booking.errors.purposeRequired': 'Visit purpose is required',
    'booking.errors.dateRequired': 'Date is required',
    'booking.errors.endDateRequired': 'End date is required',
    'booking.errors.endDateBeforeStart': 'End date must be after start date',
    'booking.errors.startTimeRequired': 'Start time is required',
    'booking.errors.endTimeRequired': 'End time is required',
    'booking.errors.submitFailed': 'Failed to submit booking request',
    'booking.errors.locationsLoadFailed': 'Failed to load available locations',

    // Common
    'common.currency': 'SAR',
    'common.learnMore': 'Learn More',
    'common.contactUs': 'Contact Us',
    'common.getStarted': 'Get Started',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.saving': 'Saving...',
    'validation.phoneSaudiFormat':
      'Enter 9 digits (no leading 0) or 10 digits (with 0). Saudi format: +966.',
    'validation.phoneRequired': 'Phone number is required.',
    'validation.driverIdMustBe10Digits': 'Driver ID must be exactly 10 digits.',
    'validation.driverIdRequired': 'Driver ID is required.',
    'validation.driverIdFormat': 'Driver ID must be exactly 10 digits.',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.close': 'Close',
    'common.export': 'Export',
    'common.error': 'Error',
    'common.retry': 'Try Again',
    'common.comingSoon': 'Coming Soon',
    'common.loading': 'Loading...',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.status': 'Status',
    'common.clear': 'Clear',
    'common.all': 'All',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.share': 'Share',
    'common.sharing': 'Sharing...',
    'common.shareFileFailed': 'Unable to share file on this device.',

    // Date Range Picker
    'dateRangePicker.placeholder': 'Select date range',
    'dateRangePicker.presetsLabel': 'Quick Select',
    'dateRangePicker.startLabel': 'Start',
    'dateRangePicker.endLabel': 'End',
    'dateRangePicker.clearLabel': 'Reset selection',
    'dateRangePicker.selectDatePlaceholder': 'Select date',
    'dateRangePicker.today': 'Today',
    'dateRangePicker.thisWeek': 'This Week',
    'dateRangePicker.thisMonth': 'This Month',
    'dateRangePicker.last30Days': 'Last 30 Days',
    'dateRangePicker.last90Days': 'Last 90 Days',
    'dateRangePicker.thisQuarter': 'This Quarter',
    'dateRangePicker.thisYear': 'This Year',
    'dateRangePicker.next30Days': 'Next 30 Days',
    'dateRangePicker.next90Days': 'Next 90 Days',

    // Footer
    'footer.company': 'Company',
    'footer.products': 'Products',
    'footer.support': 'Support',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacy Policy',
    'footer.rights': 'All rights reserved',
    'footer.description':
      'United Feed Company - Reliable livestock and poultry feed, delivered safely across Saudi Arabia.',

    // ============================================================================
    // Addressbook - Page & Section
    // ============================================================================
    'addressbook.title': 'Address Book',
    'addressbook.subtitle': 'Manage your locations and addresses',
    'addressbook.searchPlaceholder': 'Search addresses...',
    'addressbook.noAddresses': 'No Addresses Found',
    'addressbook.noAddressesDescription':
      "You haven't added any addresses yet. Add your first address to get started.",
    'addressbook.addFirst': 'Add Your First Address',
    'addressbook.exportComingSoon':
      'Export functionality will be available soon.',

    // Addressbook - Drawer Headers
    'addressbook.addAddress': 'Add New Address',
    'addressbook.editAddress': 'Edit Address',
    'addressbook.addAddressDescription':
      'Fill in the details to add a new address',
    'addressbook.editAddressDescription': 'Update the address details below',

    // Addressbook - Section Headers
    'addressbook.basicInfo': 'Basic Information',
    'addressbook.addressDetails': 'Address Details',
    'addressbook.contactInfo': 'Contact Information',
    'addressbook.coordinates': 'Coordinates',

    // Addressbook - Form Fields
    'addressbook.locationName': 'Location Name',
    'addressbook.locationNamePlaceholder': 'e.g., Main Office, Warehouse A',
    'addressbook.locationType': 'Location Type',
    'addressbook.selectType': 'Select location type',
    'addressbook.streetAddress': 'Street Address',
    'addressbook.streetAddressPlaceholder':
      'Enter street address, building name, etc.',
    'addressbook.region': 'Region',
    'addressbook.selectRegion': 'Select region',
    'addressbook.city': 'City',
    'addressbook.selectCity': 'Select city',
    'addressbook.selectRegionFirst': 'Select region first',
    'addressbook.country': 'Country',
    'addressbook.selectCountry': 'Select country',
    'addressbook.postalCode': 'Postal Code',
    'addressbook.postalCodePlaceholder': 'Enter postal code',
    'addressbook.latitude': 'Latitude',
    'addressbook.longitude': 'Longitude',
    'addressbook.contactName': 'Contact Name',
    'addressbook.contactNamePlaceholder': 'Enter contact person name',
    'addressbook.contactPhone': 'Contact Phone',
    'addressbook.contactPhonePlaceholder': '+966 50 XXX XXXX',
    'addressbook.contactEmail': 'Contact Email',
    'addressbook.address': 'Address',
    'addressbook.notes': 'Notes',
    'addressbook.tags': 'Tags',
    'addressbook.timezone': 'Timezone',

    // Addressbook - Address Types (used in type selector and badges)
    'addressbook.site': 'Site',
    'addressbook.office': 'Office',
    'addressbook.warehouse': 'Warehouse',
    'addressbook.home': 'Home',
    'addressbook.plant': 'Plant',
    'addressbook.customer': 'Customer',

    // Addressbook - Status
    'addressbook.defaultAddress': 'Default Address',
    'addressbook.favorite': 'Favorite',

    // Addressbook - Toast Messages
    'addressbook.addressCreated': 'Address Created',
    'addressbook.addressCreatedDescription':
      'The new address has been added successfully.',
    'addressbook.addressUpdated': 'Address Updated',
    'addressbook.addressUpdatedDescription':
      'The address details have been updated.',
    'addressbook.addressDeleted': 'Address Deleted',
    'addressbook.addressDeletedDescription': 'The address has been removed.',

    // Addressbook - Delete Dialog
    'addressbook.deleteTitle': 'Delete Address',
    'addressbook.deleteDescription':
      'Are you sure you want to delete this address? This action cannot be undone.',
    'addressbook.deleteConfirm': 'Yes, Delete',

    // Addressbook - Filter Options
    'addressbook.filterAll': 'All Types',
    'addressbook.filterSite': 'Sites',
    'addressbook.filterOffice': 'Offices',
    'addressbook.filterWarehouse': 'Warehouses',
    'addressbook.filterHome': 'Home',
    'addressbook.filterPlant': 'Plants',
    'addressbook.filterCustomer': 'Customers',

    // Addressbook - Stats
    'addressbook.totalAddresses': 'Total Addresses',
    'addressbook.activeAddresses': 'Active',
    'addressbook.inactiveAddresses': 'Inactive',

    // Addressbook - Table Headers
    'addressbook.table.name': 'Name',
    'addressbook.table.type': 'Type',
    'addressbook.table.address': 'Address',
    'addressbook.table.city': 'City',
    'addressbook.table.contact': 'Contact',
    'addressbook.table.status': 'Status',

    // Addressbook - Validation
    'addressbook.validation.nameRequired': 'Location name is required',
    'addressbook.validation.addressRequired': 'Street address is required',
    'addressbook.validation.countryRequired': 'Country is required',
    'addressbook.validation.cityRequired': 'City is required',

    // Addressbook - Additional Form
    'addressbook.selectCountryFirst': 'Select country first',
    'addressbook.noCitiesAvailable': 'No cities available',
    'addressbook.form.addressLine2': 'Address Line 2',
    'addressbook.form.addressLine2.placeholder':
      'Apartment, suite, unit, etc. (optional)',
    'addressbook.form.addressLine2Placeholder':
      'Apartment, suite, unit, etc. (optional)',
    'addressbook.form.englishSection': 'English',
    'addressbook.form.streetSection': 'Street address',
    'addressbook.form.locationNameEn': 'Location name (English)',
    'addressbook.form.locationNameAr': 'Location name (Arabic)',
    'addressbook.form.locationNameArPlaceholder': 'Arabic name (optional)',
    'addressbook.form.streetAddressEn': 'Street address (English)',
    'addressbook.form.streetAddressAr': 'Street address (Arabic)',
    'addressbook.form.streetAddressArPlaceholder':
      'Arabic street address (optional)',
    'addressbook.form.addressLine2En': 'Address line 2 (English)',
    'addressbook.form.addressLine2Ar': 'Address line 2 (Arabic)',
    'addressbook.form.addressLine2ArPlaceholder': 'Arabic line 2 (optional)',

    // Addressbook - Contact Types
    'addressbook.contactType.gate_manager': 'Gate Manager',
    'addressbook.contactType.dock_supervisor': 'Dock Supervisor',
    'addressbook.contactType.security': 'Security',
    'addressbook.contactType.primary_contact': 'Primary Contact',
    'addressbook.contactType.emergency_contact': 'Emergency Contact',

    // Addressbook - Relations
    'addressbook.locationType.site': 'Construction Site',
    'addressbook.locationType.office': 'Office Building',
    'addressbook.locationType.warehouse': 'Warehouse',
    'addressbook.locationType.home': 'Home Address',
    'addressbook.locationType.plant': 'Manufacturing Plant',
    'addressbook.locationType.customer': 'Customer Location',

    // Saudi Arabia Regions
    'region.riyadh': 'Riyadh Region',
    'region.makkah': 'Makkah Region',
    'region.eastern': 'Eastern Region',
    'region.madinah': 'Madinah Region',
    'region.qassim': 'Qassim Region',
    'region.asir': 'Asir Region',
    'region.tabuk': 'Tabuk Region',
    'region.hail': 'Hail Region',
    'region.northern': 'Northern Borders',
    'region.jazan': 'Jazan Region',
    'region.najran': 'Najran Region',
    'region.bahah': 'Al Bahah Region',
    'region.jawf': 'Al Jawf Region',

    // ============================================================================
    // Factories - Page & Section
    // ============================================================================
    'factories.title': 'Factories',
    'factories.subtitle':
      'Manage manufacturing plants and production facilities',
    'factories.addFactory': 'Add Factory',
    'factories.editFactory': 'Edit Factory',
    'factories.deleteFactory': 'Delete Factory',
    'factories.factoryDetails': 'Factory Details',
    'factories.searchPlaceholder': 'Search factories...',
    'factories.filterByStatus': 'Filter by status',
    'factories.noFactories': 'No Factories Found',
    'factories.noFactoriesDescription':
      "You haven't added any factories yet. Add your first factory to get started.",
    'factories.addFirst': 'Add Your First Factory',
    'factories.totalFactories': 'Total Factories',

    // Factories - Form Fields
    'factories.form.code': 'Factory Code',
    'factories.form.codePlaceholder': 'Enter factory code (e.g., FAC-001)',
    'factories.form.nameEn': 'Factory Name (English)',
    'factories.form.nameEnPlaceholder': 'Enter factory name in English',
    'factories.form.nameAr': 'Factory Name (Arabic)',
    'factories.form.nameArPlaceholder': 'Enter factory name in Arabic',
    'factories.form.address': 'Address',
    'factories.form.addressPlaceholder': 'Enter street address',
    'factories.form.city': 'City',
    'factories.form.selectCity': 'Select city',
    'factories.form.country': 'Country',
    'factories.form.selectCountry': 'Select country',
    'factories.form.postalCode': 'Postal Code',
    'factories.form.timezone': 'Timezone',
    'factories.form.coordinates': 'Coordinates',
    'factories.form.status': 'Status',

    // Factories - Validation
    'factories.validation.codeRequired': 'Factory code is required',
    'factories.validation.nameRequired': 'Factory name is required',
    'factories.validation.countryRequired': 'Country is required',

    // Factories - Status
    'factories.status.active': 'Active',
    'factories.status.inactive': 'Inactive',

    // Factories - Messages
    'factories.messages.createSuccess': 'Factory created successfully',
    'factories.messages.updateSuccess': 'Factory updated successfully',
    'factories.messages.deleteSuccess': 'Factory deleted successfully',
    'factories.messages.deleteConfirm':
      'Are you sure you want to delete this factory?',

    // Sidebar - Factories
    'sidebar.factories': 'Factories',
  },
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.products': 'المنتجات',
    'nav.booking': 'زيارة المواقع',
    'nav.about': 'من نحن',
    'nav.contact': 'اتصل بنا',
    'auth.signIn': 'تسجيل الدخول',
    'auth.createAccount': 'إنشاء حساب',
    'auth.account': 'الحساب',
    'auth.signOut': 'تسجيل الخروج',
    'auth.dashboard': 'لوحة التحكم',

    // Location Selection Dialog
    'locationDialog.tag': 'إعداد الموقع',
    'locationDialog.title': 'اختر موقعك',
    'locationDialog.description':
      'اختر المدينة والمصنع المفضل لتخصيص المنتجات المعروضة.',
    'locationDialog.step.city': 'المدينة',
    'locationDialog.step.factory': 'المصنع',
    'locationDialog.step.cityTitle': 'اختر المدينة',
    'locationDialog.step.citySubtitle': 'حدد المدينة لعرض المصانع المتاحة.',
    'locationDialog.step.factoryTitle': 'اختر المصنع',
    'locationDialog.step.factorySubtitle': 'حدد المصنع المفضل للطلب.',
    'locationDialog.empty.cities': 'لا توجد مدن متاحة.',
    'locationDialog.empty.factories': 'لا توجد مصانع متاحة لهذه المدينة.',
    'locationDialog.error.cities': 'فشل تحميل المدن.',
    'locationDialog.error.factories': 'فشل تحميل المصانع.',
    'locationDialog.selectedCity': 'المدينة المختارة',
    'locationDialog.actions.changeCity': 'تغيير المدينة',
    'locationDialog.actions.back': 'رجوع',
    'locationDialog.actions.close': 'إغلاق',
    'locationDialog.helper': 'اختيار المصنع يحدد المنتجات المتاحة.',

    // Header
    'header.selectFactory': 'اختر المصنع',
    'header.searchFactories': 'البحث عن المصانع...',
    'header.allFactories': 'جميع المصانع',

    // Hero Section
    'hero.title': 'الشركة المتحدة للأعلاف',
    'hero.subtitle':
      'إنتاج موثوق لأعلاف الثروة الحيوانية والدواجن مع خدمات لوجستية من المصنع إلى العميل في أنحاء المملكة',
    'hero.cta.primary': 'استكشف المنتجات',
    'hero.cta.secondary': 'احجز زيارة المصنع',
    'hero.stat1.value': '39',
    'hero.stat1.label': 'طن طاقة يومية',
    'hero.stat2.value': '3',
    'hero.stat2.label': 'مصانع تصنيع',
    'hero.stat3.value': '+50',
    'hero.stat3.label': 'عاماً من التميز',
    'hero.stat4.value': '0',
    'hero.stat4.label': 'حوادث',

    // About Page
    'about.hero.eyebrow': 'عن الشركة المتحدة للأعلاف',
    'about.hero.title': 'ندعم الأمن الغذائي بأعلاف ولوجستيات موثوقة',
    'about.hero.subtitle':
      'الشركة المتحدة للأعلاف شركة سعودية تخدم عملاء الثروة الحيوانية والدواجن بمنتجات موثوقة وخدمات لوجستية متكاملة.',
    'about.hero.since': 'تأسست 1398هـ',
    'about.certs.title': 'شهاداتنا',
    'about.certs.subtitle': 'معترف بنا عالمياً في الجودة والسلامة والامتثال.',
    'about.certs.downloadPdf': 'تحميل PDF',
    'about.certs.viewCertificate': 'عرض الشهادة',
    'about.certs.empty': 'لا توجد شهادات متاحة في الوقت الحالي.',
    'about.certs.loadError': 'تعذر تحميل الشهادات.',
    'about.stats.title': 'حقائق أساسية',
    'about.stats.subtitle': 'لمحة عن حجمنا وقدرتنا الإنتاجية.',
    'about.story.title': 'قصتنا',
    'about.story.subtitle':
      'إرث من الاعتمادية مبني على معايير الجودة السعودية.',
    'about.story.paragraph1':
      'تدير الشركة المتحدة للأعلاف إنتاج الأعلاف وخدمات النقل من ينبع إلى العملاء في أنحاء المملكة.',
    'about.story.paragraph2':
      'نوفر أعلاف الثروة الحيوانية والدواجن بجودة ثابتة وخدمات لوجستية موثوقة.',
    'about.story.paragraph3':
      'يرتكز عملنا على السلامة والتميز التشغيلي ونجاح العملاء.',
    'about.vision.title': 'رؤيتنا',
    'about.vision.description':
      'أن نكون الشريك الموثوق للأعلاف في المملكة عبر الجودة والموثوقية والاستدامة.',
    'about.mission.title': 'رسالتنا',
    'about.mission.description':
      'توفير منتجات موثوقة وخدمة استثنائية مع تمكين فرقنا وشركائنا.',
    'about.values.title': 'قيمنا',
    'about.values.subtitle': 'المبادئ التي توجه قراراتنا اليومية.',
    'about.values.customer.title': 'التركيز على العميل',
    'about.values.customer.description':
      'نصمم الخدمة والتسليم حول نتائج العميل.',
    'about.values.integrity.title': 'النزاهة',
    'about.values.integrity.description': 'نلتزم بالشفافية والمساءلة.',
    'about.values.excellence.title': 'التميز',
    'about.values.excellence.description':
      'نرفع معايير الجودة والسلامة والأداء.',
    'about.values.sustainability.title': 'الاستدامة',
    'about.values.sustainability.description':
      'نقلل الأثر عبر عمليات كفؤة ومسؤولة.',
    'about.values.teamwork.title': 'العمل الجماعي',
    'about.values.teamwork.description':
      'نتعاون عبر المصانع والفرق لتحقيق تسليم موثوق.',
    'about.values.innovation.title': 'الابتكار',
    'about.values.innovation.description':
      'نستثمر في العمليات الحديثة والتحسين المستمر.',
    'about.operations.title': 'بصمتنا التصنيعية',
    'about.operations.subtitle': 'إنتاج وخدمات لوجستية متكاملة في مصنع أعلاف ينبع.',
    'about.operations.plantJazan.name': 'إنتاج الأعلاف في ينبع',
    'about.operations.plantJazan.description':
      'إنتاج الأعلاف والخدمات اللوجستية في ينبع لخدمة العملاء في أنحاء المملكة.',
    'about.operations.plantYanbu.name': 'تحميل الأعلاف السائبة في ينبع',
    'about.operations.plantYanbu.description':
      'صوامع وتحميل الأعلاف السائبة لأسطول الشاحنات المستأجر.',
    'about.operations.plantTihama.name': 'الأعلاف المعبأة في ينبع',
    'about.operations.plantTihama.description':
      'خطوط إنتاج حديثة مصممة للموثوقية والاستدامة.',
    'about.commitments.title': 'الجودة والسلامة والاستدامة',
    'about.commitments.subtitle': 'معايير تقود كل طن ننتجه.',
    'about.commitments.quality.title': 'ضمان الجودة',
    'about.commitments.quality.description':
      'اختبارات صارمة وتحكم في العمليات لكل دفعة.',
    'about.commitments.safety.title': 'السلامة أولاً',
    'about.commitments.safety.description':
      'حماية فرقنا وشركائنا بأنظمة سلامة منضبطة.',
    'about.commitments.sustainability.title': 'عمليات مستدامة',
    'about.commitments.sustainability.description':
      'تحسين الكفاءة والاستخدام المسؤول للموارد.',

    // Contact Page
    'contact.hero.eyebrow': 'تواصل معنا',
    'contact.hero.title': 'تواصل مع الفريق الذي يحافظ على تقدم مشاريعك',
    'contact.hero.subtitle':
      'شاركنا احتياجك وسنساعدك بالمنتج والجدول والدعم المناسب.',
    'contact.hero.ctaPrimary': 'اتصل بالمبيعات',
    'contact.hero.ctaSecondary': 'أرسل رسالة',
    'contact.hero.highlight.sales': 'استفسارات المبيعات',
    'contact.hero.highlight.support': 'الدعم الفني',
    'contact.hero.highlight.partnerships': 'الشراكات',
    'contact.hero.panelTitle': 'قنوات التواصل',
    'contact.hero.panelBadge': 'نحن هنا',

    'contact.cards.phone.title': 'الهاتف',
    'contact.cards.phone.value': '+966-XX-XXX-XXXX',
    'contact.cards.phone.description': 'خدمة العملاء والمبيعات',
    'contact.cards.email.title': 'البريد الإلكتروني',
    'contact.cards.email.value': 'info@ufc.sa',
    'contact.cards.email.description': 'للاستفسارات العامة',
    'contact.cards.location.title': 'نطاق التغطية',
    'contact.cards.location.value': 'جازان · بيشة · تهامة',
    'contact.cards.location.description':
      'خدمة العملاء في أنحاء المملكة العربية السعودية',

    // Contact Form
    'contact.form.title': 'أرسل لنا رسالة',
    'contact.form.subtitle':
      'املأ النموذج أدناه وسيتواصل معك فريقنا خلال 24 ساعة.',
    'contact.form.name': 'الاسم الكامل',
    'contact.form.namePlaceholder': 'اسمك الكامل',
    'contact.form.email': 'البريد الإلكتروني',
    'contact.form.emailPlaceholder': 'بريدك@الإلكتروني.com',
    'contact.form.phone': 'رقم الهاتف',
    'contact.form.phonePlaceholder': '+966 5X XXX XXXX',
    'contact.form.phoneOptional': 'اختياري',
    'contact.form.subject': 'الموضوع',
    'contact.form.subjectPlaceholder': 'اختر موضوعاً',
    'contact.form.subject.generalInquiry': 'استفسار عام',
    'contact.form.subject.productInfo': 'معلومات المنتج',
    'contact.form.subject.technicalSupport': 'الدعم الفني',
    'contact.form.subject.partnership': 'شراكة',
    'contact.form.subject.other': 'أخرى',
    'contact.form.message': 'الرسالة',
    'contact.form.messagePlaceholder': 'كيف يمكننا مساعدتك؟',
    'contact.form.submit': 'إرسال الرسالة',
    'contact.form.submitting': 'جارٍ الإرسال...',
    'contact.form.success.title': 'تم إرسال الرسالة!',
    'contact.form.success.description':
      'شكراً لتواصلك. سنرد عليك على {{email}} في غضون 24 ساعة.',
    'contact.form.success.sendAnother': 'إرسال رسالة أخرى',
    'contact.form.error.nameRequired': 'الاسم الكامل مطلوب',
    'contact.form.error.emailRequired': 'البريد الإلكتروني مطلوب',
    'contact.form.error.emailInvalid': 'يرجى إدخال بريد إلكتروني صحيح',
    'contact.form.error.subjectRequired': 'يرجى اختيار موضوع',
    'contact.form.error.messageRequired': 'الرسالة مطلوبة',
    'contact.form.error.messageTooShort':
      'يجب أن تكون الرسالة 10 أحرف على الأقل',
    'contact.form.error.failed': 'فشل إرسال رسالتك. يرجى المحاولة مرة أخرى.',

    // Support Tickets (E-commerce)
    'support.successTitle': 'تم إرسال التذكرة!',
    'support.successMessage': 'تم استلام طلبك وسنعاود التواصل معك قريباً.',
    'support.submitAnother': 'إرسال تذكرة أخرى',
    'support.fieldSubject': 'الموضوع',
    'support.subjectPlaceholder': 'ملخص سريع للمشكلة',
    'support.fieldCategory': 'الفئة',
    'support.selectCategory': 'اختر الفئة',
    'support.categoryOrder': 'مشكلة في الطلب',
    'support.categoryDelivery': 'مشكلة في التسليم',
    'support.categoryPayment': 'مشكلة في الدفع',
    'support.categoryAccount': 'مشكلة في الحساب',
    'support.categoryGeneral': 'عام',
    'support.categoryOther': 'أخرى',
    'support.fieldDescription': 'الوصف',
    'support.descriptionPlaceholder': 'اشرح المشكلة بالتفصيل...',
    'support.fieldEmail': 'بريد للتواصل',
    'support.emailPlaceholder': 'email@example.com',
    'support.fieldPhone': 'هاتف للتواصل',
    'support.phonePlaceholder': '+966 5X XXX XXXX',
    'support.createError': 'تعذر الإرسال. يرجى المحاولة مرة أخرى.',
    'support.submitting': 'جارٍ الإرسال...',
    'support.submit': 'إرسال تذكرة',

    // Not Found
    'ui.notFound.title': '404 - الصفحة غير موجودة',
    'ui.notFound.description': 'عذراً، الصفحة التي تبحث عنها غير موجودة.',
    'ui.notFound.goHome': 'الصفحة الرئيسية',

    // Products
    'products.title': 'منتجاتنا من الأعلاف',
    'products.subtitle':
      'أعلاف عالية الجودة للثروة الحيوانية والدواجن في أنحاء المملكة',
    'products.viewDetails': 'عرض التفاصيل',
    'products.addToCart': 'أضف إلى السلة',
    'products.selectVariation': 'اختر العبوة',
    'products.inStock': 'متوفر',
    'products.category': 'الفئة',
    'products.certificates.title': 'شهادات المنتج',
    'products.certificates.downloadPdf': 'تحميل PDF',
    'products.certificates.empty': 'لا توجد شهادات متاحة لهذا المنتج.',
    'cart.title': 'سلة التسوق',
    'cart.itemsInCart': 'عنصر في سلة التسوق',
    'cart.orderSummary': 'ملخص الطلب',
    'cart.total': 'الإجمالي',
    'cart.proceedToCheckout': 'المتابعة للدفع',
    'cart.continueShopping': 'متابعة التسوق',
    'cart.empty.title': 'سلة التسوق فارغة',
    'cart.empty.description': 'أضف بعض المنتجات إلى سلة التسوق للبدء',
    'cart.empty.browseProducts': 'تصفح المنتجات',

    // Plant Visits
    'booking.title': 'زيارة مرافقنا',
    'booking.subtitle': 'حدد موعدًا لزيارة مرافقنا ذات المستوى العالمي',
    'booking.book': 'احجز زيارة',
    'booking.duration': 'المدة',
    'booking.capacity': 'حجم المجموعة',
    'booking.price': 'السعر',
    'booking.free': 'مجاناً',
    'booking.popular': 'الأكثر شعبية',
    'booking.errors.firstNameRequired': 'الاسم الأول مطلوب',
    'booking.errors.lastNameRequired': 'اسم العائلة مطلوب',
    'booking.errors.userIdRequired': 'رقم الهوية مطلوب',
    'booking.errors.userIdInvalid': 'يرجى إدخال رقم هوية صالح',
    'booking.errors.emailInvalid': 'يرجى إدخال بريد إلكتروني صالح',
    'booking.errors.phoneRequired': 'رقم الجوال مطلوب',
    'booking.errors.phoneInvalid': 'يرجى إدخال رقم جوال صالح',
    'booking.errors.locationRequired': 'الموقع مطلوب',
    'booking.errors.hostNameRequired': 'اسم المضيف مطلوب',
    'booking.errors.hostPhoneRequired': 'رقم المضيف مطلوب',
    'booking.errors.purposeRequired': 'الغرض من الزيارة مطلوب',
    'booking.errors.dateRequired': 'التاريخ مطلوب',
    'booking.errors.endDateRequired': 'تاريخ النهاية مطلوب',
    'booking.errors.endDateBeforeStart':
      'يجب أن يكون تاريخ النهاية بعد تاريخ البداية',
    'booking.errors.startTimeRequired': 'وقت البداية مطلوب',
    'booking.errors.endTimeRequired': 'وقت النهاية مطلوب',
    'booking.errors.submitFailed': 'فشل إرسال طلب الزيارة',
    'booking.errors.locationsLoadFailed': 'فشل تحميل المواقع المتاحة',

    // Common
    'common.currency': 'ريال',
    'common.learnMore': 'اعرف المزيد',
    'common.contactUs': 'اتصل بنا',
    'common.getStarted': 'ابدأ الآن',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.saving': 'جاري الحفظ...',
    'validation.phoneSaudiFormat':
      'أدخل 9 أرقام (بدون 0 في البداية) أو 10 أرقام (مع 0). التنسيق السعودي: +966.',
    'validation.phoneRequired': 'رقم الهاتف مطلوب.',
    'validation.driverIdMustBe10Digits':
      'معرف السائق يجب أن يكون 10 أرقام بالضبط.',
    'validation.driverIdRequired': 'معرف السائق مطلوب.',
    'validation.driverIdFormat': 'معرف السائق يجب أن يكون 10 أرقام بالضبط.',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.close': 'إغلاق',
    'common.export': 'تصدير',
    'common.error': 'خطأ',
    'common.retry': 'إعادة المحاولة',
    'common.comingSoon': 'قريباً',
    'common.loading': 'جاري التحميل...',
    'common.search': 'بحث',
    'common.filter': 'تصفية',
    'common.status': 'الحالة',
    'common.clear': 'مسح',
    'common.all': 'الكل',
    'common.active': 'نشط',
    'common.inactive': 'غير نشط',
    'common.share': 'مشاركة',
    'common.sharing': 'جاري المشاركة...',
    'common.shareFileFailed': 'تعذر مشاركة الملف على هذا الجهاز.',

    // Date Range Picker
    'dateRangePicker.placeholder': 'اختر نطاق التاريخ',
    'dateRangePicker.presetsLabel': 'اختيار سريع',
    'dateRangePicker.startLabel': 'البداية',
    'dateRangePicker.endLabel': 'النهاية',
    'dateRangePicker.clearLabel': 'مسح',
    'dateRangePicker.selectDatePlaceholder': 'اختر تاريخ',
    'dateRangePicker.today': 'اليوم',
    'dateRangePicker.thisWeek': 'هذا الأسبوع',
    'dateRangePicker.thisMonth': 'هذا الشهر',
    'dateRangePicker.last30Days': 'آخر 30 يوم',
    'dateRangePicker.last90Days': 'آخر 90 يوم',
    'dateRangePicker.thisQuarter': 'هذا الربع',
    'dateRangePicker.thisYear': 'هذا العام',
    'dateRangePicker.next30Days': 'الـ 30 يوم القادمة',
    'dateRangePicker.next90Days': 'الـ 90 يوم القادمة',

    // Footer
    'footer.company': 'الشركة',
    'footer.products': 'المنتجات',
    'footer.support': 'الدعم',
    'footer.legal': 'قانوني',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.rights': 'جميع الحقوق محفوظة',
    'footer.description':
      'الشركة المتحدة للأعلاف - أعلاف موثوقة للثروة الحيوانية والدواجن تُسلّم بأمان في أنحاء المملكة.',

    // ============================================================================
    // Addressbook - Page & Section
    // ============================================================================
    'addressbook.title': 'دفتر العناوين',
    'addressbook.subtitle': 'إدارة المواقع والعناوين الخاصة بك',
    'addressbook.searchPlaceholder': 'البحث في العناوين...',
    'addressbook.noAddresses': 'لا توجد عناوين',
    'addressbook.noAddressesDescription':
      'لم تقم بإضافة أي عناوين بعد. أضف عنوانك الأول للبدء.',
    'addressbook.addFirst': 'أضف عنوانك الأول',
    'addressbook.exportComingSoon': 'ستتوفر وظيفة التصدير قريباً.',

    // Addressbook - Drawer Headers
    'addressbook.addAddress': 'إضافة عنوان جديد',
    'addressbook.editAddress': 'تعديل العنوان',
    'addressbook.addAddressDescription': 'أدخل التفاصيل لإضافة عنوان جديد',
    'addressbook.editAddressDescription': 'قم بتحديث تفاصيل العنوان أدناه',

    // Addressbook - Section Headers
    'addressbook.basicInfo': 'المعلومات الأساسية',
    'addressbook.addressDetails': 'تفاصيل العنوان',
    'addressbook.contactInfo': 'معلومات الاتصال',
    'addressbook.coordinates': 'الإحداثيات',

    // Addressbook - Form Fields
    'addressbook.locationName': 'اسم الموقع',
    'addressbook.locationNamePlaceholder': 'مثال: المكتب الرئيسي، المستودع أ',
    'addressbook.locationType': 'نوع الموقع',
    'addressbook.selectType': 'اختر نوع الموقع',
    'addressbook.streetAddress': 'عنوان الشارع',
    'addressbook.streetAddressPlaceholder':
      'أدخل عنوان الشارع، اسم المبنى، إلخ.',
    'addressbook.region': 'المنطقة',
    'addressbook.selectRegion': 'اختر المنطقة',
    'addressbook.city': 'المدينة',
    'addressbook.selectCity': 'اختر المدينة',
    'addressbook.selectRegionFirst': 'اختر المنطقة أولاً',
    'addressbook.country': 'الدولة',
    'addressbook.selectCountry': 'اختر الدولة',
    'addressbook.postalCode': 'الرمز البريدي',
    'addressbook.postalCodePlaceholder': 'أدخل الرمز البريدي',
    'addressbook.latitude': 'خط العرض',
    'addressbook.longitude': 'خط الطول',
    'addressbook.contactName': 'اسم جهة الاتصال',
    'addressbook.contactNamePlaceholder': 'أدخل اسم الشخص المسؤول',
    'addressbook.contactPhone': 'هاتف جهة الاتصال',
    'addressbook.contactPhonePlaceholder': '+966 50 XXX XXXX',
    'addressbook.contactEmail': 'البريد الإلكتروني',
    'addressbook.address': 'العنوان',
    'addressbook.notes': 'ملاحظات',
    'addressbook.tags': 'العلامات',
    'addressbook.timezone': 'المنطقة الزمنية',

    // Addressbook - Address Types (used in type selector and badges)
    'addressbook.site': 'موقع',
    'addressbook.office': 'مكتب',
    'addressbook.warehouse': 'مستودع',
    'addressbook.home': 'منزل',
    'addressbook.plant': 'مصنع',
    'addressbook.customer': 'عميل',

    // Addressbook - Status
    'addressbook.defaultAddress': 'العنوان الافتراضي',
    'addressbook.favorite': 'مفضل',

    // Addressbook - Toast Messages
    'addressbook.addressCreated': 'تم إنشاء العنوان',
    'addressbook.addressCreatedDescription': 'تمت إضافة العنوان الجديد بنجاح.',
    'addressbook.addressUpdated': 'تم تحديث العنوان',
    'addressbook.addressUpdatedDescription': 'تم تحديث تفاصيل العنوان.',
    'addressbook.addressDeleted': 'تم حذف العنوان',
    'addressbook.addressDeletedDescription': 'تمت إزالة العنوان.',

    // Addressbook - Delete Dialog
    'addressbook.deleteTitle': 'حذف العنوان',
    'addressbook.deleteDescription':
      'هل أنت متأكد من حذف هذا العنوان؟ لا يمكن التراجع عن هذا الإجراء.',
    'addressbook.deleteConfirm': 'نعم، احذف',

    // Addressbook - Filter Options
    'addressbook.filterAll': 'جميع الأنواع',
    'addressbook.filterSite': 'المواقع',
    'addressbook.filterOffice': 'المكاتب',
    'addressbook.filterWarehouse': 'المستودعات',
    'addressbook.filterHome': 'المنازل',
    'addressbook.filterPlant': 'المصانع',
    'addressbook.filterCustomer': 'العملاء',

    // Addressbook - Stats
    'addressbook.totalAddresses': 'إجمالي العناوين',
    'addressbook.activeAddresses': 'نشط',
    'addressbook.inactiveAddresses': 'غير نشط',

    // Addressbook - Table Headers
    'addressbook.table.name': 'الاسم',
    'addressbook.table.type': 'النوع',
    'addressbook.table.address': 'العنوان',
    'addressbook.table.city': 'المدينة',
    'addressbook.table.contact': 'جهة الاتصال',
    'addressbook.table.status': 'الحالة',

    // Addressbook - Contact Types
    'addressbook.contactType.gate_manager': 'مدير البوابة',
    'addressbook.contactType.dock_supervisor': 'مشرف الرصيف',
    'addressbook.contactType.security': 'الأمن',
    'addressbook.contactType.primary_contact': 'جهة الاتصال الرئيسية',
    'addressbook.contactType.emergency_contact': 'جهة اتصال الطوارئ',

    // Addressbook - Validation
    'addressbook.validation.nameRequired': 'اسم الموقع مطلوب',
    'addressbook.validation.addressRequired': 'عنوان الشارع مطلوب',
    'addressbook.validation.countryRequired': 'الدولة مطلوبة',
    'addressbook.validation.cityRequired': 'المدينة مطلوبة',

    // Addressbook - Additional Form
    'addressbook.selectCountryFirst': 'اختر الدولة أولاً',
    'addressbook.noCitiesAvailable': 'لا توجد مدن متاحة',
    'addressbook.form.addressLine2': 'سطر العنوان 2',
    'addressbook.form.addressLine2.placeholder':
      'شقة، جناح، وحدة، إلخ. (اختياري)',
    'addressbook.form.addressLine2Placeholder':
      'شقة، جناح، وحدة، إلخ. (اختياري)',
    'addressbook.form.englishSection': 'الإنجليزية',
    'addressbook.form.streetSection': 'عنوان الشارع',
    'addressbook.form.locationNameEn': 'اسم الموقع (إنجليزي)',
    'addressbook.form.locationNameAr': 'اسم الموقع (عربي)',
    'addressbook.form.locationNameArPlaceholder': 'الاسم بالعربية (اختياري)',
    'addressbook.form.streetAddressEn': 'عنوان الشارع (إنجليزي)',
    'addressbook.form.streetAddressAr': 'عنوان الشارع (عربي)',
    'addressbook.form.streetAddressArPlaceholder':
      'عنوان الشارع بالعربية (اختياري)',
    'addressbook.form.addressLine2En': 'سطر العنوان 2 (إنجليزي)',
    'addressbook.form.addressLine2Ar': 'سطر العنوان 2 (عربي)',
    'addressbook.form.addressLine2ArPlaceholder': 'سطر 2 بالعربية (اختياري)',

    // Addressbook - Relations
    'addressbook.locationType.site': 'موقع بناء',
    'addressbook.locationType.office': 'مبنى مكاتب',
    'addressbook.locationType.warehouse': 'مستودع',
    'addressbook.locationType.home': 'عنوان المنزل',
    'addressbook.locationType.plant': 'مصنع تصنيع',
    'addressbook.locationType.customer': 'موقع العميل',

    // Saudi Arabia Regions
    'region.riyadh': 'منطقة الرياض',
    'region.makkah': 'منطقة مكة المكرمة',
    'region.eastern': 'المنطقة الشرقية',
    'region.madinah': 'منطقة المدينة المنورة',
    'region.qassim': 'منطقة القصيم',
    'region.asir': 'منطقة عسير',
    'region.tabuk': 'منطقة تبوك',
    'region.hail': 'منطقة حائل',
    'region.northern': 'منطقة الحدود الشمالية',
    'region.jazan': 'منطقة جازان',
    'region.najran': 'منطقة نجران',
    'region.bahah': 'منطقة الباحة',
    'region.jawf': 'منطقة الجوف',

    // ============================================================================
    // Factories - Page & Section
    // ============================================================================
    'factories.title': 'المصانع',
    'factories.subtitle': 'إدارة مصانع الإنتاج والمنشآت الصناعية',
    'factories.addFactory': 'إضافة مصنع',
    'factories.editFactory': 'تعديل المصنع',
    'factories.deleteFactory': 'حذف المصنع',
    'factories.factoryDetails': 'تفاصيل المصنع',
    'factories.searchPlaceholder': 'البحث عن المصانع...',
    'factories.filterByStatus': 'تصفية حسب الحالة',
    'factories.noFactories': 'لا توجد مصانع',
    'factories.noFactoriesDescription':
      'لم تقم بإضافة أي مصانع بعد. أضف أول مصنع للبدء.',
    'factories.addFirst': 'أضف أول مصنع',
    'factories.totalFactories': 'إجمالي المصانع',

    // Factories - Form Fields
    'factories.form.code': 'رمز المصنع',
    'factories.form.codePlaceholder': 'أدخل رمز المصنع (مثال: FAC-001)',
    'factories.form.nameEn': 'اسم المصنع (بالإنجليزية)',
    'factories.form.nameEnPlaceholder': 'أدخل اسم المصنع بالإنجليزية',
    'factories.form.nameAr': 'اسم المصنع (بالعربية)',
    'factories.form.nameArPlaceholder': 'أدخل اسم المصنع بالعربية',
    'factories.form.address': 'العنوان',
    'factories.form.addressPlaceholder': 'أدخل عنوان الشارع',
    'factories.form.city': 'المدينة',
    'factories.form.selectCity': 'اختر المدينة',
    'factories.form.country': 'الدولة',
    'factories.form.selectCountry': 'اختر الدولة',
    'factories.form.postalCode': 'الرمز البريدي',
    'factories.form.timezone': 'المنطقة الزمنية',
    'factories.form.coordinates': 'الإحداثيات',
    'factories.form.status': 'الحالة',

    // Factories - Validation
    'factories.validation.codeRequired': 'رمز المصنع مطلوب',
    'factories.validation.nameRequired': 'اسم المصنع مطلوب',
    'factories.validation.countryRequired': 'الدولة مطلوبة',

    // Factories - Status
    'factories.status.active': 'نشط',
    'factories.status.inactive': 'غير نشط',

    // Factories - Messages
    'factories.messages.createSuccess': 'تم إنشاء المصنع بنجاح',
    'factories.messages.updateSuccess': 'تم تحديث المصنع بنجاح',
    'factories.messages.deleteSuccess': 'تم حذف المصنع بنجاح',
    'factories.messages.deleteConfirm': 'هل أنت متأكد من حذف هذا المصنع؟',

    // Sidebar - Factories
    'sidebar.factories': 'المصانع',
  },
  ur: {
    // Navigation
    'nav.home': 'ہوم',
    'nav.products': 'مصنوعات',
    'nav.booking': 'مقام کا دورہ',
    'nav.about': 'ہمارے بارے میں',
    'nav.contact': 'رابطہ',
    'auth.signIn': 'سائن ان',
    'auth.createAccount': 'اکاؤنٹ بنائیں',
    'auth.account': 'اکاؤنٹ',
    'auth.signOut': 'سائن آؤٹ',
    'auth.dashboard': 'ڈیش بورڈ',

    // Location Selection Dialog
    'locationDialog.tag': 'لوکیشن سیٹ اپ',
    'locationDialog.title': 'اپنا مقام منتخب کریں',
    'locationDialog.description':
      'شہر اور پسندیدہ فیکٹری منتخب کریں تاکہ کیٹلاگ ذاتی نوعیت کا ہو۔',
    'locationDialog.step.city': 'شہر',
    'locationDialog.step.factory': 'فیکٹری',
    'locationDialog.step.cityTitle': 'شہر منتخب کریں',
    'locationDialog.step.citySubtitle':
      'فیکٹریاں دیکھنے کے لیے شہر منتخب کریں۔',
    'locationDialog.step.factoryTitle': 'فیکٹری منتخب کریں',
    'locationDialog.step.factorySubtitle': 'اپنی پسندیدہ فیکٹری منتخب کریں۔',
    'locationDialog.empty.cities': 'کوئی شہر دستیاب نہیں۔',
    'locationDialog.empty.factories': 'اس شہر کے لیے کوئی فیکٹری دستیاب نہیں۔',
    'locationDialog.error.cities': 'شہروں کو لوڈ کرنے میں ناکام۔',
    'locationDialog.error.factories': 'فیکٹریاں لوڈ کرنے میں ناکام۔',
    'locationDialog.selectedCity': 'منتخب شہر',
    'locationDialog.actions.changeCity': 'شہر تبدیل کریں',
    'locationDialog.actions.back': 'واپس',
    'locationDialog.actions.close': 'بند کریں',
    'locationDialog.helper': 'فیکٹری منتخب کرنے سے دستیاب مصنوعات بدلتی ہیں۔',

    // Header
    'header.selectFactory': 'پلانٹ منتخب کریں',
    'header.searchFactories': 'پلانٹس تلاش کریں...',
    'header.allFactories': 'تمام پلانٹس',

    // Hero Section
    'hero.title': 'United Feed Company',
    'hero.subtitle':
      'سعودی عرب بھر میں فیکٹری سے صارف تک قابل اعتماد مویشی اور پولٹری فیڈ اور لاجسٹکس',
    'hero.cta.primary': 'مصنوعات دیکھیں',
    'hero.cta.secondary': 'فیکٹری وزٹ بک کریں',
    'hero.stat1.value': '39',
    'hero.stat1.label': 'روزانہ گنجائش (ٹن)',
    'hero.stat2.value': '3',
    'hero.stat2.label': 'مینوفیکچرنگ پلانٹس',
    'hero.stat3.value': '50+',
    'hero.stat3.label': 'سالوں کی عمدگی',
    'hero.stat4.value': '0',
    'hero.stat4.label': 'حادثات',

    // About Page
    'about.hero.eyebrow': 'United Feed Company کے بارے میں',
    'about.hero.title':
      'قابل اعتماد فیڈ اور لاجسٹکس کے ذریعے غذائی تحفظ کی حمایت',
    'about.hero.subtitle':
      'United Feed Company سعودی عرب میں مویشیوں اور پولٹری کے صارفین کو معیاری فیڈ اور قابل اعتماد لاجسٹکس فراہم کرتی ہے۔',
    'about.hero.since': '1398 ہجری میں قائم',
    'about.certs.title': 'ہمارے سرٹیفکیٹس',
    'about.certs.subtitle':
      'معیار، حفاظت اور تعمیل میں عالمی سطح پر تسلیم شدہ۔',
    'about.certs.downloadPdf': 'PDF ڈاؤن لوڈ کریں',
    'about.certs.viewCertificate': 'سرٹیفکیٹ دیکھیں',
    'about.certs.empty': 'اس وقت کوئی سرٹیفکیٹ دستیاب نہیں ہے۔',
    'about.certs.loadError': 'سرٹیفکیٹس لوڈ نہیں ہو سکے۔',
    'about.stats.title': 'اہم حقائق',
    'about.stats.subtitle': 'ہماری وسعت اور پیداواری صلاحیت کی جھلک۔',
    'about.story.title': 'ہماری کہانی',
    'about.story.subtitle': 'قابل اعتماد وراثت، سعودی معیار پر مبنی۔',
    'about.story.paragraph1':
      'United Feed Company جدید فیڈ پیداوار اور فیکٹری سے صارف تک لاجسٹکس کے ذریعے سعودی عرب بھر میں خدمات فراہم کرتی ہے۔',
    'about.story.paragraph2':
      'ہم مستقل معیار اور قابل اعتماد فیکٹری سے صارف تک لاجسٹکس کے ساتھ مویشی اور پولٹری فیڈ فراہم کرتے ہیں۔',
    'about.story.paragraph3':
      'ہمارے لوگ، عمل اور پلانٹس حفاظت، آپریشنل بہترینگی اور کسٹمر کامیابی پر مرکوز ہیں۔',
    'about.vision.title': 'ہمارا وژن',
    'about.vision.description':
      'معیار، اعتماد اور پائیداری کے ذریعے مملکت کا قابل اعتماد فیڈ پارٹنر بننا۔',
    'about.mission.title': 'ہمارا مشن',
    'about.mission.description':
      'قابل اعتماد مصنوعات اور شاندار خدمت فراہم کرنا، اپنی ٹیموں اور شراکت داروں کو مضبوط بنانا۔',
    'about.values.title': 'ہماری اقدار',
    'about.values.subtitle':
      'وہ اصول جو ہمارے روزمرہ فیصلوں کی رہنمائی کرتے ہیں۔',
    'about.values.customer.title': 'کسٹمر مرکزیت',
    'about.values.customer.description':
      'ہم خدمت اور ترسیل کو کسٹمر نتائج کے گرد ڈیزائن کرتے ہیں۔',
    'about.values.integrity.title': 'دیانت داری',
    'about.values.integrity.description':
      'ہم شفافیت اور جواب دہی کے ساتھ درست کام کرتے ہیں۔',
    'about.values.excellence.title': 'عمدگی',
    'about.values.excellence.description':
      'ہم معیار، حفاظت اور کارکردگی میں معیار بلند کرتے ہیں۔',
    'about.values.sustainability.title': 'پائیداری',
    'about.values.sustainability.description':
      'موثر اور ذمہ دار آپریشنز کے ذریعے اثر کم کرتے ہیں۔',
    'about.values.teamwork.title': 'ٹیم ورک',
    'about.values.teamwork.description':
      'ہم پلانٹس اور ٹیموں کے درمیان مل کر قابل اعتماد ترسیل کرتے ہیں۔',
    'about.values.innovation.title': 'جدت',
    'about.values.innovation.description':
      'ہم جدید عمل اور مسلسل بہتری میں سرمایہ کاری کرتے ہیں۔',
    'about.operations.title': 'مینوفیکچرنگ نیٹ ورک',
    'about.operations.subtitle': 'ینبع فیڈ مل میں مربوط پیداوار اور لاجسٹکس۔',
    'about.operations.plantJazan.name': 'ینبع فیڈ پیداوار',
    'about.operations.plantJazan.description':
      'ینبع سے سعودی عرب بھر کے صارفین کے لیے فیڈ پیداوار اور مؤثر لاجسٹکس۔',
    'about.operations.plantYanbu.name': 'ینبع بلک لوڈنگ',
    'about.operations.plantYanbu.description':
      'کرائے کے بلکر فلیٹ کے لیے بلک فیڈ سائلوز اور لوڈنگ آپریشنز۔',
    'about.operations.plantTihama.name': 'ینبع پیک شدہ فیڈ',
    'about.operations.plantTihama.description':
      'جدید پروڈکشن لائنیں جو قابل اعتماد اور پائیدار پیداوار کے لیے ڈیزائن کی گئی ہیں۔',
    'about.commitments.title': 'معیار، حفاظت اور پائیداری',
    'about.commitments.subtitle': 'وہ معیار جو ہر ٹن کی رہنمائی کرتے ہیں۔',
    'about.commitments.quality.title': 'معیار کی یقین دہانی',
    'about.commitments.quality.description':
      'ہر بیچ پر سخت جانچ اور عمل کا کنٹرول۔',
    'about.commitments.safety.title': 'حفاظت پہلے',
    'about.commitments.safety.description':
      'مضبوط حفاظتی نظام کے ذریعے اپنی ٹیموں اور شراکت داروں کی حفاظت۔',
    'about.commitments.sustainability.title': 'پائیدار آپریشنز',
    'about.commitments.sustainability.description':
      'پلانٹس میں کارکردگی میں بہتری اور وسائل کا ذمہ دارانہ استعمال۔',

    // Contact Page
    'contact.hero.eyebrow': 'ہم سے رابطہ',
    'contact.hero.title':
      'اس ٹیم سے بات کریں جو آپ کے منصوبوں کو آگے بڑھاتی ہے',
    'contact.hero.subtitle':
      'اپنے تقاضے شیئر کریں، ہم درست پروڈکٹ، شیڈول اور سپورٹ کے ساتھ مدد کریں گے۔',
    'contact.hero.ctaPrimary': 'سیلز کو کال کریں',
    'contact.hero.ctaSecondary': 'پیغام بھیجیں',
    'contact.hero.highlight.sales': 'سیلز سوالات',
    'contact.hero.highlight.support': 'تکنیکی سپورٹ',
    'contact.hero.highlight.partnerships': 'شراکت داری',
    'contact.hero.panelTitle': 'رابطے کے چینلز',
    'contact.hero.panelBadge': 'ہم حاضر ہیں',

    'contact.cards.phone.title': 'فون',
    'contact.cards.phone.value': '+966-XX-XXX-XXXX',
    'contact.cards.phone.description': 'کسٹمر کیئر اور سیلز',
    'contact.cards.email.title': 'ای میل',
    'contact.cards.email.value': 'info@ufc.sa',
    'contact.cards.email.description': 'عمومی استفسارات',
    'contact.cards.location.title': 'کوریج',
    'contact.cards.location.value': 'جازان · بیشہ · تہامہ',
    'contact.cards.location.description': 'سعودی عرب بھر میں صارفین کی خدمت',

    // Contact Form
    'contact.form.title': 'ہمیں پیغام بھیجیں',
    'contact.form.subtitle':
      'نیچے دیا گیا فارم پُر کریں اور ہماری ٹیم 24 گھنٹوں میں آپ سے رابطہ کرے گی۔',
    'contact.form.name': 'پورا نام',
    'contact.form.namePlaceholder': 'آپ کا پورا نام',
    'contact.form.email': 'ای میل پتہ',
    'contact.form.emailPlaceholder': 'آپکی@ای میل.com',
    'contact.form.phone': 'فون نمبر',
    'contact.form.phonePlaceholder': '+966 5X XXX XXXX',
    'contact.form.phoneOptional': 'اختیاری',
    'contact.form.subject': 'موضوع',
    'contact.form.subjectPlaceholder': 'موضوع منتخب کریں',
    'contact.form.subject.generalInquiry': 'عمومی سوال',
    'contact.form.subject.productInfo': 'مصنوع کی معلومات',
    'contact.form.subject.technicalSupport': 'تکنیکی مدد',
    'contact.form.subject.partnership': 'شراکت',
    'contact.form.subject.other': 'دیگر',
    'contact.form.message': 'پیغام',
    'contact.form.messagePlaceholder': 'ہم آپ کی کیسے مدد کر سکتے ہیں؟',
    'contact.form.submit': 'پیغام بھیجیں',
    'contact.form.submitting': 'بھیجا جا رہا ہے...',
    'contact.form.success.title': 'پیغام بھیج دیا گیا!',
    'contact.form.success.description':
      'رابطہ کرنے کا شکریہ۔ ہم 24 گھنٹوں میں {{email}} پر جواب دیں گے۔',
    'contact.form.success.sendAnother': 'ایک اور پیغام بھیجیں',
    'contact.form.error.nameRequired': 'پورا نام ضروری ہے',
    'contact.form.error.emailRequired': 'ای میل پتہ ضروری ہے',
    'contact.form.error.emailInvalid': 'براہ کرم درست ای میل پتہ درج کریں',
    'contact.form.error.subjectRequired': 'موضوع منتخب کریں',
    'contact.form.error.messageRequired': 'پیغام ضروری ہے',
    'contact.form.error.messageTooShort':
      'پیغام کم از کم 10 حروف کا ہونا چاہیے',
    'contact.form.error.failed': 'پیغام بھیجنے میں ناکامی۔ دوبارہ کوشش کریں۔',

    // Support Tickets (E-commerce)
    'support.successTitle': 'ٹکٹ جمع ہو گیا!',
    'support.successMessage':
      'ہمیں آپ کی درخواست موصول ہو گئی ہے اور ہم جلد آپ سے رابطہ کریں گے۔',
    'support.submitAnother': 'ایک اور ٹکٹ جمع کریں',
    'support.fieldSubject': 'موضوع',
    'support.subjectPlaceholder': 'اپنے مسئلے کا مختصر خلاصہ',
    'support.fieldCategory': 'زمرہ',
    'support.selectCategory': 'زمرہ منتخب کریں',
    'support.categoryOrder': 'آرڈر کا مسئلہ',
    'support.categoryDelivery': 'ڈیلیوری کا مسئلہ',
    'support.categoryPayment': 'ادائیگی کا مسئلہ',
    'support.categoryAccount': 'اکاؤنٹ کا مسئلہ',
    'support.categoryGeneral': 'عمومی',
    'support.categoryOther': 'دیگر',
    'support.fieldDescription': 'تفصیل',
    'support.descriptionPlaceholder': 'اپنے مسئلے کی تفصیل لکھیں...',
    'support.fieldEmail': 'رابطہ ای میل',
    'support.emailPlaceholder': 'email@example.com',
    'support.fieldPhone': 'رابطہ فون',
    'support.phonePlaceholder': '+966 5X XXX XXXX',
    'support.createError': 'جمع نہیں ہو سکا۔ دوبارہ کوشش کریں۔',
    'support.submitting': 'جمع کیا جا رہا ہے...',
    'support.submit': 'ٹکٹ جمع کریں',

    // Not Found
    'ui.notFound.title': '404 - صفحہ نہیں ملا',
    'ui.notFound.description': 'معذرت، آپ جو صفحہ تلاش کر رہے ہیں وہ موجود نہیں ہے۔',
    'ui.notFound.goHome': 'گھر جائیں',

    // Products
    'products.title': 'ہماری فیڈ مصنوعات',
    'products.subtitle':
      'سعودی عرب بھر کے فارموں کے لیے اعلیٰ معیار کی مویشی اور پولٹری فیڈ',
    'products.viewDetails': 'تفصیلات دیکھیں',
    'products.addToCart': 'کارٹ میں شامل کریں',
    'products.selectVariation': 'پیکیج منتخب کریں',
    'products.inStock': 'دستیاب',
    'products.category': 'زمرہ',
    'products.certificates.title': 'پروڈکٹ سرٹیفکیٹس',
    'products.certificates.downloadPdf': 'PDF ڈاؤن لوڈ کریں',
    'products.certificates.empty':
      'اس پروڈکٹ کے لیے کوئی سرٹیفکیٹ دستیاب نہیں ہے۔',
    'cart.title': 'شاپنگ کارٹ',
    'cart.itemsInCart': 'آئٹمز آپ کے کارٹ میں',
    'cart.orderSummary': 'آرڈر خلاصہ',
    'cart.total': 'کل',
    'cart.proceedToCheckout': 'چیک آؤٹ پر جائیں',
    'cart.continueShopping': 'خریداری جاری رکھیں',
    'cart.empty.title': 'آپ کی کارٹ خالی ہے',
    'cart.empty.description': 'شروع کرنے کے لیے کچھ مصنوعات کارٹ میں شامل کریں',
    'cart.empty.browseProducts': 'مصنوعات دیکھیں',

    // Plant Visits
    'booking.title': 'ہماری سہولیات کا دورہ کریں',
    'booking.subtitle': 'ہماری عالمی معیار کی سہولیات کے دورے کا شیڈول بنائیں',
    'booking.book': 'وزٹ بک کریں',
    'booking.duration': 'مدت',
    'booking.capacity': 'گروپ سائز',
    'booking.price': 'قیمت',
    'booking.free': 'مفت',
    'booking.popular': 'سب سے مقبول',
    'booking.errors.firstNameRequired': 'پہلا نام ضروری ہے',
    'booking.errors.lastNameRequired': 'آخری نام ضروری ہے',
    'booking.errors.userIdRequired': 'شناختی نمبر ضروری ہے',
    'booking.errors.userIdInvalid': 'براہِ کرم درست شناختی نمبر درج کریں',
    'booking.errors.emailInvalid': 'براہِ کرم درست ای میل درج کریں',
    'booking.errors.phoneRequired': 'فون نمبر ضروری ہے',
    'booking.errors.phoneInvalid': 'براہِ کرم درست فون نمبر درج کریں',
    'booking.errors.locationRequired': 'لوکیشن ضروری ہے',
    'booking.errors.hostNameRequired': 'میزبان کا نام ضروری ہے',
    'booking.errors.hostPhoneRequired': 'میزبان کا فون نمبر ضروری ہے',
    'booking.errors.purposeRequired': 'وزٹ کا مقصد ضروری ہے',
    'booking.errors.dateRequired': 'تاریخ ضروری ہے',
    'booking.errors.endDateRequired': 'اختتامی تاریخ ضروری ہے',
    'booking.errors.endDateBeforeStart':
      'اختتامی تاریخ، ابتدائی تاریخ کے بعد ہونی چاہیے',
    'booking.errors.startTimeRequired': 'ابتدائی وقت ضروری ہے',
    'booking.errors.endTimeRequired': 'اختتامی وقت ضروری ہے',
    'booking.errors.submitFailed': 'وزٹ درخواست جمع کرانے میں ناکامی',
    'booking.errors.locationsLoadFailed': 'دستیاب لوکیشنز لوڈ کرنے میں ناکامی',

    // Common
    'common.currency': 'ریال',
    'common.learnMore': 'مزید جانیں',
    'common.contactUs': 'ہم سے رابطہ کریں',
    'common.getStarted': 'شروع کریں',
    'common.cancel': 'منسوخ',
    'common.save': 'محفوظ کریں',
    'common.saving': 'محفوظ ہو رہا ہے...',
    'validation.phoneSaudiFormat':
      '9 ہندسے درج کریں (0 کے بغیر) یا 10 ہندسے (0 کے ساتھ)۔ سعودی فارمیٹ: +966۔',
    'validation.phoneRequired': 'فون نمبر درکار ہے۔',
    'validation.driverIdMustBe10Digits':
      'ڈرائیور آئی ڈی بالکل 10 ہندسے ہونی چاہیے۔',
    'validation.driverIdRequired': 'ڈرائیور آئی ڈی درکار ہے۔',
    'validation.driverIdFormat': 'ڈرائیور آئی ڈی بالکل 10 ہندسے ہونی چاہیے۔',
    'common.edit': 'ترمیم',
    'common.delete': 'حذف کریں',
    'common.close': 'بند کریں',
    'common.export': 'برآمد',
    'common.error': 'خرابی',
    'common.retry': 'دوبارہ کوشش کریں',
    'common.comingSoon': 'جلد آرہا ہے',
    'common.loading': 'لوڈ ہو رہا ہے...',
    'common.search': 'تلاش',
    'common.filter': 'فلٹر',
    'common.status': 'حالت',
    'common.clear': 'صاف کریں',
    'common.all': 'سب',
    'common.active': 'فعال',
    'common.inactive': 'غیر فعال',
    'common.share': 'شیئر کریں',
    'common.sharing': 'شیئر ہو رہا ہے...',
    'common.shareFileFailed': 'اس ڈیوائس پر فائل شیئر نہیں ہو سکی۔',

    // Date Range Picker
    'dateRangePicker.placeholder': 'تاریخ کی حد منتخب کریں',
    'dateRangePicker.presetsLabel': 'فوری انتخاب',
    'dateRangePicker.startLabel': 'ابتداء',
    'dateRangePicker.endLabel': 'اختتام',
    'dateRangePicker.clearLabel': 'صاف کریں',
    'dateRangePicker.selectDatePlaceholder': 'تاریخ منتخب کریں',
    'dateRangePicker.today': 'آج',
    'dateRangePicker.thisWeek': 'اس ہفتے',
    'dateRangePicker.thisMonth': 'اس مہینے',
    'dateRangePicker.last30Days': 'گزشتہ 30 دن',
    'dateRangePicker.last90Days': 'گزشتہ 90 دن',
    'dateRangePicker.thisQuarter': 'اس سہ ماہی',
    'dateRangePicker.thisYear': 'اس سال',
    'dateRangePicker.next30Days': 'اگلے 30 دن',
    'dateRangePicker.next90Days': 'اگلے 90 دن',

    // Footer
    'footer.company': 'کمپنی',
    'footer.products': 'مصنوعات',
    'footer.support': 'سپورٹ',
    'footer.legal': 'قانونی',
    'footer.privacy': 'رازداری کی پالیسی',
    'footer.rights': 'جملہ حقوق محفوظ ہیں',
    'footer.description':
      'United Feed Company - مویشیوں اور پولٹری کے لیے قابل اعتماد فیڈ، سعودی عرب بھر میں محفوظ ترسیل کے ساتھ۔',

    // ============================================================================
    // Addressbook - Page & Section
    // ============================================================================
    'addressbook.title': 'ایڈریس بک',
    'addressbook.subtitle': 'اپنے مقامات اور پتے منظم کریں',
    'addressbook.searchPlaceholder': 'پتوں میں تلاش کریں...',
    'addressbook.noAddresses': 'کوئی پتہ نہیں ملا',
    'addressbook.noAddressesDescription':
      'آپ نے ابھی کوئی پتہ شامل نہیں کیا۔ شروع کرنے کے لیے پہلا پتہ شامل کریں۔',
    'addressbook.addFirst': 'اپنا پہلا پتہ شامل کریں',
    'addressbook.exportComingSoon': 'برآمد کی سہولت جلد دستیاب ہوگی۔',

    // Addressbook - Drawer Headers
    'addressbook.addAddress': 'نیا پتہ شامل کریں',
    'addressbook.editAddress': 'پتہ ترمیم کریں',
    'addressbook.addAddressDescription':
      'نیا پتہ شامل کرنے کے لیے تفصیلات درج کریں',
    'addressbook.editAddressDescription': 'ذیل میں پتے کی تفصیلات اپ ڈیٹ کریں',

    // Addressbook - Section Headers
    'addressbook.basicInfo': 'بنیادی معلومات',
    'addressbook.addressDetails': 'پتے کی تفصیلات',
    'addressbook.contactInfo': 'رابطہ معلومات',
    'addressbook.coordinates': 'کوآرڈینیٹس',

    // Addressbook - Form Fields
    'addressbook.locationName': 'مقام کا نام',
    'addressbook.locationNamePlaceholder': 'مثال: مرکزی دفتر، گودام A',
    'addressbook.locationType': 'مقام کی قسم',
    'addressbook.selectType': 'مقام کی قسم منتخب کریں',
    'addressbook.streetAddress': 'گلی کا پتہ',
    'addressbook.streetAddressPlaceholder':
      'گلی کا پتہ، عمارت کا نام وغیرہ درج کریں',
    'addressbook.region': 'علاقہ',
    'addressbook.selectRegion': 'علاقہ منتخب کریں',
    'addressbook.city': 'شہر',
    'addressbook.selectCity': 'شہر منتخب کریں',
    'addressbook.selectRegionFirst': 'پہلے علاقہ منتخب کریں',
    'addressbook.country': 'ملک',
    'addressbook.selectCountry': 'ملک منتخب کریں',
    'addressbook.postalCode': 'پوسٹل کوڈ',
    'addressbook.postalCodePlaceholder': 'پوسٹل کوڈ درج کریں',
    'addressbook.latitude': 'عرضِ بلد',
    'addressbook.longitude': 'طولِ بلد',
    'addressbook.contactName': 'رابطہ شخص کا نام',
    'addressbook.contactNamePlaceholder': 'رابطہ شخص کا نام درج کریں',
    'addressbook.contactPhone': 'رابطہ فون',
    'addressbook.contactPhonePlaceholder': '+966 50 XXX XXXX',
    'addressbook.contactEmail': 'رابطہ ای میل',
    'addressbook.address': 'پتہ',
    'addressbook.notes': 'نوٹس',
    'addressbook.tags': 'ٹیگز',
    'addressbook.timezone': 'ٹائم زون',

    // Addressbook - Address Types (used in type selector and badges)
    'addressbook.site': 'سائٹ',
    'addressbook.office': 'دفتر',
    'addressbook.warehouse': 'گودام',
    'addressbook.home': 'گھر',
    'addressbook.plant': 'پلانٹ',
    'addressbook.customer': 'گاہک',

    // Addressbook - Status
    'addressbook.defaultAddress': 'طے شدہ پتہ',
    'addressbook.favorite': 'پسندیدہ',

    // Addressbook - Toast Messages
    'addressbook.addressCreated': 'پتہ بنایا گیا',
    'addressbook.addressCreatedDescription':
      'نیا پتہ کامیابی سے شامل کر دیا گیا ہے۔',
    'addressbook.addressUpdated': 'پتہ اپ ڈیٹ ہوا',
    'addressbook.addressUpdatedDescription':
      'پتے کی تفصیلات اپ ڈیٹ کر دی گئی ہیں۔',
    'addressbook.addressDeleted': 'پتہ حذف ہوا',
    'addressbook.addressDeletedDescription': 'پتہ ہٹا دیا گیا ہے۔',

    // Addressbook - Delete Dialog
    'addressbook.deleteTitle': 'پتہ حذف کریں',
    'addressbook.deleteDescription':
      'کیا آپ واقعی یہ پتہ حذف کرنا چاہتے ہیں؟ یہ عمل واپس نہیں ہو سکے گا۔',
    'addressbook.deleteConfirm': 'جی ہاں، حذف کریں',

    // Addressbook - Filter Options
    'addressbook.filterAll': 'تمام اقسام',
    'addressbook.filterSite': 'سائٹس',
    'addressbook.filterOffice': 'دفاتر',
    'addressbook.filterWarehouse': 'گودام',
    'addressbook.filterHome': 'گھر',
    'addressbook.filterPlant': 'پلانٹس',
    'addressbook.filterCustomer': 'گاہک',

    // Addressbook - Stats
    'addressbook.totalAddresses': 'کل پتے',
    'addressbook.activeAddresses': 'فعال',
    'addressbook.inactiveAddresses': 'غیر فعال',

    // Addressbook - Table Headers
    'addressbook.table.name': 'نام',
    'addressbook.table.type': 'قسم',
    'addressbook.table.address': 'پتہ',
    'addressbook.table.city': 'شہر',
    'addressbook.table.contact': 'رابطہ',
    'addressbook.table.status': 'حالت',

    // Addressbook - Contact Types
    'addressbook.contactType.gate_manager': 'گیٹ منیجر',
    'addressbook.contactType.dock_supervisor': 'ڈاک سپروائزر',
    'addressbook.contactType.security': 'سیکیورٹی',
    'addressbook.contactType.primary_contact': 'بنیادی رابطہ',
    'addressbook.contactType.emergency_contact': 'ہنگامی رابطہ',

    // Addressbook - Validation
    'addressbook.validation.nameRequired': 'مقام کا نام درکار ہے',
    'addressbook.validation.addressRequired': 'گلی کا پتہ درکار ہے',
    'addressbook.validation.countryRequired': 'ملک درکار ہے',
    'addressbook.validation.cityRequired': 'شہر درکار ہے',

    // Addressbook - Additional Form
    'addressbook.selectCountryFirst': 'پہلے ملک منتخب کریں',
    'addressbook.noCitiesAvailable': 'کوئی شہر دستیاب نہیں',
    'addressbook.form.addressLine2': 'پتے کی دوسری لائن',
    'addressbook.form.addressLine2.placeholder':
      'اپارٹمنٹ، سوٹ، یونٹ وغیرہ (اختیاری)',
    'addressbook.form.addressLine2Placeholder':
      'اپارٹمنٹ، سویٹ، یونٹ وغیرہ (اختیاری)',
    'addressbook.form.englishSection': 'انگریزی',
    'addressbook.form.streetSection': 'گلی کا پتہ',
    'addressbook.form.locationNameEn': 'مقام کا نام (انگریزی)',
    'addressbook.form.locationNameAr': 'مقام کا نام (عربی)',
    'addressbook.form.locationNameArPlaceholder': 'عربی نام (اختیاری)',
    'addressbook.form.streetAddressEn': 'گلی کا پتہ (انگریزی)',
    'addressbook.form.streetAddressAr': 'گلی کا پتہ (عربی)',
    'addressbook.form.streetAddressArPlaceholder':
      'عربی میں گلی کا پتہ (اختیاری)',
    'addressbook.form.addressLine2En': 'پتہ لائن 2 (انگریزی)',
    'addressbook.form.addressLine2Ar': 'پتہ لائن 2 (عربی)',
    'addressbook.form.addressLine2ArPlaceholder': 'لائن 2 عربی میں (اختیاری)',

    // Addressbook - Relations
    'addressbook.locationType.site': 'تعمیراتی سائٹ',
    'addressbook.locationType.office': 'دفتر کی عمارت',
    'addressbook.locationType.warehouse': 'گودام',
    'addressbook.locationType.home': 'گھر کا پتہ',
    'addressbook.locationType.plant': 'مینوفیکچرنگ پلانٹ',
    'addressbook.locationType.customer': 'گاہک کا مقام',

    // Saudi Arabia Regions
    'region.riyadh': 'ریاض ریجن',
    'region.makkah': 'مکہ ریجن',
    'region.eastern': 'شرقی ریجن',
    'region.madinah': 'مدینہ ریجن',
    'region.qassim': 'قصیم ریجن',
    'region.asir': 'عسیر ریجن',
    'region.tabuk': 'تبوک ریجن',
    'region.hail': 'حائل ریجن',
    'region.northern': 'شمالی سرحدی ریجن',
    'region.jazan': 'جازان ریجن',
    'region.najran': 'نجران ریجن',
    'region.bahah': 'باحہ ریجن',
    'region.jawf': 'جوف ریجن',

    // ============================================================================
    // Factories - Page & Section
    // ============================================================================
    'factories.title': 'فیکٹریاں',
    'factories.subtitle': 'پیداواری فیکٹریوں اور صنعتی سہولیات کا انتظام کریں',
    'factories.addFactory': 'فیکٹری شامل کریں',
    'factories.editFactory': 'فیکٹری ترمیم کریں',
    'factories.deleteFactory': 'فیکٹری حذف کریں',
    'factories.factoryDetails': 'فیکٹری کی تفصیلات',
    'factories.searchPlaceholder': 'فیکٹریاں تلاش کریں...',
    'factories.filterByStatus': 'حالت کے مطابق فلٹر',
    'factories.noFactories': 'کوئی فیکٹری نہیں',
    'factories.noFactoriesDescription':
      'ابھی تک کوئی فیکٹری شامل نہیں کی۔ شروع کرنے کے لیے پہلی فیکٹری شامل کریں۔',
    'factories.addFirst': 'پہلی فیکٹری شامل کریں',
    'factories.totalFactories': 'کل فیکٹریاں',

    // Factories - Form Fields
    'factories.form.code': 'فیکٹری کوڈ',
    'factories.form.codePlaceholder': 'فیکٹری کوڈ درج کریں (مثلاً: FAC-001)',
    'factories.form.nameEn': 'فیکٹری کا نام (انگریزی)',
    'factories.form.nameEnPlaceholder': 'فیکٹری کا نام انگریزی میں درج کریں',
    'factories.form.nameAr': 'فیکٹری کا نام (عربی)',
    'factories.form.nameArPlaceholder': 'فیکٹری کا نام عربی میں درج کریں',
    'factories.form.address': 'پتہ',
    'factories.form.addressPlaceholder': 'گلی کا پتہ درج کریں',
    'factories.form.city': 'شہر',
    'factories.form.selectCity': 'شہر منتخب کریں',
    'factories.form.country': 'ملک',
    'factories.form.selectCountry': 'ملک منتخب کریں',
    'factories.form.postalCode': 'پوسٹل کوڈ',
    'factories.form.timezone': 'ٹائم زون',
    'factories.form.coordinates': 'کوآرڈینیٹس',
    'factories.form.status': 'حالت',

    // Factories - Validation
    'factories.validation.codeRequired': 'فیکٹری کوڈ ضروری ہے',
    'factories.validation.nameRequired': 'فیکٹری کا نام ضروری ہے',
    'factories.validation.countryRequired': 'ملک ضروری ہے',

    // Factories - Status
    'factories.status.active': 'فعال',
    'factories.status.inactive': 'غیر فعال',

    // Factories - Messages
    'factories.messages.createSuccess': 'فیکٹری کامیابی سے بن گئی',
    'factories.messages.updateSuccess': 'فیکٹری کامیابی سے اپ ڈیٹ ہوئی',
    'factories.messages.deleteSuccess': 'فیکٹری کامیابی سے حذف ہوئی',
    'factories.messages.deleteConfirm':
      'کیا آپ واقعی اس فیکٹری کو حذف کرنا چاہتے ہیں؟',

    // Sidebar - Factories
    'sidebar.factories': 'فیکٹریاں',
  },
};

export function getTranslation(locale: Locale, key: string): string {
  return translations[locale][key as keyof (typeof translations)['en']] || key;
}
